import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';

import { PaginationControl } from 'shared/components/pagination';
import { ApiKey, HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import {
   Lead,
   LeadsIndexRequest,
   LeadsIndexResponse,
   LeadSortDirection,
   LeadSortKey,
   LeadStatus,
} from './types';

const PER_PAGE = 20;
export const ALL_LEAD_STATUSES_VALUE = 'ALL';

type AdminRequestOptions = {
   adminID?: string;
   tokenKey?: string;
};

const adminHeaders = ({ adminID, tokenKey }: AdminRequestOptions) => ({
   key: ApiKey,
   adminid: adminID,
   Authorization: `Bearer ${tokenKey}`,
});

export class LeadsBrowserStore {
   readonly loadLeadsState = new AsyncState<LeadsIndexResponse>();
   readonly pagination = new PaginationControl();

   rawLeads: Lead[] = [];
   searchStr = '';
   selectedStatus: LeadStatus | typeof ALL_LEAD_STATUSES_VALUE = ALL_LEAD_STATUSES_VALUE;
   sorter: LeadSortKey = 'created_at';
   direction: LeadSortDirection = 'desc';
   totalRecords = 0;
   totalPageCount = 0;
   toPage = '1';

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawLeads: observable,
         searchStr: observable,
         selectedStatus: observable,
         sorter: observable,
         direction: observable,
         totalRecords: observable,
         totalPageCount: observable,
         toPage: observable,
         leads: computed,
         totalCount: computed,
         totalPages: computed,
         loading: computed,
         recordStart: computed,
         recordEnd: computed,
         onSearchChange: action.bound,
         onStatusChange: action.bound,
         submitSearch: action.bound,
         onSortBy: action.bound,
         onToPageChange: action.bound,
         updateLeadData: action.bound,
         loadPage: action.bound,
         goToPage: action.bound,
      });
   }

   get leads(): Lead[] {
      return [...this.rawLeads].sort((a, b) => this.compareLeads(a, b));
   }

   get totalCount(): number {
      return this.totalRecords;
   }

   get totalPages(): number {
      return this.totalPageCount;
   }

   get loading(): boolean {
      return this.loadLeadsState.status === 'pending';
   }

   get recordStart(): number {
      return this.totalCount === 0 ? 0 : this.pagination.page * PER_PAGE + 1;
   }

   get recordEnd(): number {
      return Math.min(this.pagination.page * PER_PAGE + this.rawLeads.length, this.totalCount);
   }

   onSearchChange(value: string): void {
      this.searchStr = value;
   }

   onStatusChange(value: LeadStatus | typeof ALL_LEAD_STATUSES_VALUE): void {
      this.selectedStatus = value;
   }

   async submitSearch(): Promise<void> {
      await this.loadPage(0);
   }

   async onSortBy(sort: LeadSortKey): Promise<void> {
      if (this.sorter === sort) {
         this.direction = this.direction === 'asc' ? 'desc' : 'asc';
      } else {
         this.sorter = sort;
         this.direction = 'asc';
      }
   }

   onToPageChange(value: string): void {
      if (/^\d+$/.test(value) || value === '') {
         this.toPage = value;
      }
   }

   updateLeadData(id: number, data: Lead['data']): void {
      this.rawLeads = this.rawLeads.map((lead) => (lead.id === id ? { ...lead, data } : lead));
   }

   async loadPage(page: number): Promise<void> {
      const safePage = this.getSafePage(page);
      this.pagination.setPage(safePage);
      this.toPage = String(safePage + 1);
      await this.loadLeads(safePage);
   }

   async goToPage(): Promise<void> {
      if (this.totalPages === 0) {
         return;
      }

      if (this.toPage === '') {
         this.toPage = '1';
      }

      const page = Number(this.toPage);
      if (Number.isNaN(page)) {
         return;
      }

      const nextPage = Math.min(Math.max(page, 1), this.totalPages) - 1;
      await this.loadPage(nextPage);
   }

   async loadLeads(page = this.pagination.page): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.loadLeadsState.run(
         () =>
            this.loadLeadsRequest(
               {
                  page: Math.max(Math.floor(page), 0) + 1,
                  per_page: PER_PAGE,
                  search: this.searchStr.trim() || null,
                  status:
                     this.selectedStatus === ALL_LEAD_STATUSES_VALUE ? null : this.selectedStatus,
               },
               { adminID, tokenKey }
            ),
         {
            onSuccess: (resp) => {
               const response = resp.response;
               const responseList = Array.isArray(response?.data) ? response.data : [];
               const responseTotal = Number(response?.total);
               const responseLastPage = Number(response?.last_page);
               const responseCurrentPage = Number(response?.current_page);
               const nextTotalRecords = Number.isFinite(responseTotal)
                  ? responseTotal
                  : responseList.length;
               const nextTotalPages = Number.isFinite(responseLastPage)
                  ? responseLastPage
                  : Math.ceil(nextTotalRecords / PER_PAGE);
               const nextPage =
                  Number.isFinite(responseCurrentPage) && responseCurrentPage > 0
                     ? responseCurrentPage - 1
                     : page;

               runInAction(() => {
                  this.rawLeads = responseList;
                  this.totalRecords = nextTotalRecords;
                  this.totalPageCount = nextTotalPages;
                  this.pagination.setPage(nextPage);
                  this.toPage = String(nextPage + 1);
               });
               this.ensurePageInRange();
            },
         }
      );
   }

   private async loadLeadsRequest(data: LeadsIndexRequest, opt: AdminRequestOptions = {}) {
      return await HttpClient.get<LeadsIndexResponse>('admin/leads', {
         headers: adminHeaders(opt),
         params: {
            page: data.page,
            per_page: data.per_page,
            ...(data.search ? { search: data.search } : {}),
            ...(data.status ? { status: data.status } : {}),
         },
      });
   }

   private compareLeads(a: Lead, b: Lead): number {
      const left = this.getComparableValue(a);
      const right = this.getComparableValue(b);

      if (left < right) {
         return this.direction === 'asc' ? -1 : 1;
      }

      if (left > right) {
         return this.direction === 'asc' ? 1 : -1;
      }

      return 0;
   }

   private getComparableValue(lead: Lead): string | number {
      const value = lead[this.sorter];

      if (this.sorter === 'created_at' || this.sorter === 'updated_at') {
         return value ? new Date(value).getTime() : 0;
      }

      if (typeof value === 'string') {
         return value.toLocaleLowerCase();
      }

      return value ?? '';
   }

   private getSafePage(page: number): number {
      if (!Number.isFinite(page) || page < 0) {
         return 0;
      }

      if (this.totalPages === 0) {
         return Math.floor(page);
      }

      return Math.min(Math.floor(page), this.totalPages - 1);
   }

   private ensurePageInRange(): void {
      const safePage = this.getSafePage(this.pagination.page);
      if (safePage !== this.pagination.page) {
         void this.loadPage(safePage);
      }
   }
}
